import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  SettlementRequestReference,
} from '@marxbiotech/shared-types/bff';

import { api } from '@lib/api/client';
import { paymentKeys } from '@lib/query/options/payment';
import type { SettlementRequest } from '@lib/query/options/payment';

// =============================================================================
// Types
// =============================================================================

export interface CreateSettlementRequestInput {
  amount: number;
  createdByType: 'CUSTOMER';
  bankInfo: {
    account: string;
    date: string;
  };
  reference: SettlementRequestReference;
  loanRequestId?: string;
  customerRemark?: string;
}

interface CreateSettlementRequestResponse {
  settlement_request: SettlementRequest;
}

// =============================================================================
// Mutation Functions
// =============================================================================

export const createSettlementRequest = async (
  input: CreateSettlementRequestInput
): Promise<SettlementRequest> => {
  const response = await api.post<CreateSettlementRequestResponse>(
    '/payment/settlement-requests',
    input
  );
  return response.data.settlement_request;
};

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseCreateSettlementRequestOptions {
  onSuccess?: (data: SettlementRequest) => void;
  onError?: (error: Error) => void;
}

export function useCreateSettlementRequest(
  options?: UseCreateSettlementRequestOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSettlementRequest,
    onSuccess: (data, variables) => {
      // Invalidate settlement requests query
      if (variables.loanRequestId) {
        queryClient.invalidateQueries({
          queryKey: paymentKeys.settlementRequests(variables.loanRequestId),
        });
      }
      // 月結繳款通知：invalidate pending requests + monthly settlement info
      queryClient.invalidateQueries({
        queryKey: paymentKeys.pendingSettlementRequests(),
      });
      queryClient.invalidateQueries({
        queryKey: paymentKeys.monthlySettlement(),
      });

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { mutation: 'createSettlementRequest' },
      });
      options?.onError?.(error);
    },
  });
}
